import React from 'react';

type Props = {
    /** CR/EN/VU/NT species, non-invasive somewhere (the summary's Red List figure). */
    redListCount: number;
    /** GRIIS-flagged at one or more sites. */
    invasiveCount: number;
    /** Distinct species on either list — the union, so dual-status species count once. */
    totalCount: number;
    /** When given, the total reads "N predicted species across M sites". */
    siteCount?: number;
    /**
     * The grid snapshot key. Only its provenance date is shown ("Last
     * updated: July 2026") — the raw filename is a cache key, not a fact for
     * a client page. A key without a date says nothing.
     */
    snapshot?: string;
};

const gbifDateInKey = /gbif-(\d{4})-(\d{2})-\d{2}/;

const lastUpdatedLabel = (snapshot?: string): string | null => {
    const match = snapshot?.match(gbifDateInKey);
    if (!match) {
        return null;
    }
    const [, year, month] = match;
    // Built in UTC end-to-end so the month never shifts across timezones.
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

/**
 * The portfolio's priority-set figures, in words: the union total, each list's
 * own count, and how many species sit on both lists. The overlap needs no
 * extra query — the union total already avoids double-counting dual-status
 * species, so both = redList + invasive − union. An older backend whose total
 * is the plain sum yields zero overlap and the both-lists stat drops out.
 *
 * This keeps dual-status visible as a fact of the data: a species on both tabs
 * is on both lists at different sites, not a duplicate (DD-5480).
 */
const SpeciesSummaryStats = ({ redListCount, invasiveCount, totalCount, siteCount, snapshot }: Props) => {
    const both = Math.max(0, redListCount + invasiveCount - totalCount);
    const lastUpdated = lastUpdatedLabel(snapshot);

    if (totalCount <= 0) {
        return null;
    }

    const format = (n: number) => n.toLocaleString('en-GB');

    return (
        // One wrapping line, not a stack — the strip should cost the page as
        // little height as the figures allow.
        <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1' data-testid='species-summary-stats'>
            <span className='text-base text-black'>
                {format(totalCount)} predicted species
                {siteCount !== undefined && (
                    <>
                        {' '}
                        across {siteCount} site{siteCount === 1 ? '' : 's'}
                    </>
                )}
            </span>
            <span className='text-sm text-grey-80'>
                {format(invasiveCount)} invasive · {format(redListCount)} on the Red List
                {both > 0 && <> · {format(both)} on both lists</>}
            </span>
            {lastUpdated && <span className='text-xs text-grey-80'>Last updated: {lastUpdated}</span>}
        </div>
    );
};

export default SpeciesSummaryStats;
